import {
  Affix,
  AppShell,
  Button,
  Header,
  Loader,
  Navbar,
  Transition,
} from "@mantine/core";
import { useHotkeys, useScrollLock, useWindowScroll } from "@mantine/hooks";
import { openSpotlight, SpotlightProvider } from "@mantine/spotlight";
import type { SpotlightAction } from "@mantine/spotlight";
import React, { useEffect, useState } from "react";
import {
  NavigateFunction,
  Outlet,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { ArrowUp, Search } from "tabler-icons-react";

import { SearchedIssue, useGetAllIssues } from "api/issues/getAllIssues";
import { BugIcon, StoryIcon, TaskIcon } from "assets/icons";
import { Issue, User } from "types";

import HeaderContent from "./Header";
import styles from "./Layout.module.css";
import NavbarContent from "./Navbar";
// import { UnclosedSessionModal } from "components/session/unCloseSession/uncloseSession";
import { showNotification } from "@mantine/notifications";
import { useUser } from "hooks/useUser";
import { useQueryClient } from "@tanstack/react-query";
import { useGetUnclosedSessions } from "api/attendance/unCloseSession";
import UnclosedSessionModal from "components/session/unCloseSession/uncloseSession";

type LayoutProps = {
  user: User;
};
type Session = {
  sessionId: string;
  punchIn: string;
  suggestedPunchOut?: string;
};

const typeIcons: Record<Issue["type"], React.ReactNode> = {
  task: <TaskIcon />,
  story: <StoryIcon />,
  bug: <BugIcon />,
};

const getActions = (
  issues: SearchedIssue[],
  navigate: NavigateFunction
): SpotlightAction[] =>
  issues.map((issue) => ({
    id: issue._id,
    title: issue.title,
    icon: typeIcons[issue.type],
    onTrigger: () => {
      navigate(`/projects/${issue.project}?selectedIssue=${issue._id}`);
    },
  }));

function Layout({ user }: LayoutProps) {
  const { location } = window;
  const [opened, setOpened] = useState(false);
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const queryClient = useQueryClient();

  const userId = user?._id;
  const { data: unclosedData } = useGetUnclosedSessions(user._id);

  const { data, isSuccess, isFetching } = useGetAllIssues(
    user.org?._id,
    user._id
  );
  const navigate = useNavigate();

  const [scroll, scrollTo] = useWindowScroll();
  const setScrollLocked = useScrollLock()[1];

  const [searchParams] = useSearchParams();
  const selectedIssue = searchParams.get("selectedIssue");

  useHotkeys([
    [
      "mod + K",
      () => {
        if (selectedIssue) return;
        openSpotlight();
      },
    ],
  ]);

  React.useEffect(() => {
    if (unclosedData?.data && unclosedData.data.length > 0) {
      setSessions(unclosedData.data);
      setOpen(true);
    } else {
      setSessions([]);
      setOpen(false);
    }
  }, [unclosedData]);

  return (
    <SpotlightProvider
      actions={isSuccess ? getActions(data, navigate) : []}
      searchIcon={isFetching ? <Loader size={20} /> : <Search size={20} />}
      searchPlaceholder="Search issues"
      nothingFoundMessage="No issue found"
      overlayOpacity={0.5}
      overlayBlur={0}
      highlightQuery
      highlightColor="violet"
      styles={{
        actionIcon: {
          flexShrink: 0,
        },
      }}
      shortcut={null}
    >
      <AppShell
        padding={location.pathname === "/chat" ? 0 : 24}
        header={
          <Header
            height={{ base: 60, sm: 70 }}
            px="md"
            sx={{
              "@media (max-width: 319px)": {
                paddingLeft: 10,
                paddingRight: 10,
              },
            }}
          >
            <HeaderContent
              opened={opened}
              onToggleNavbar={() => {
                setOpened((o) => !o);
                setScrollLocked((l) => !l);
              }}
              onClose={() => {
                setOpened(false);
                setScrollLocked(false);
              }}
            />
          </Header>
        }
        navbar={
          <Navbar
            p="md"
            hiddenBreakpoint="sm"
            hidden={!opened}
            width={{ sm: 250, lg: 300 }}
            aria-label="Main"
            className={styles.nav}
          >
            <NavbarContent
              onClose={() => {
                setOpened(false);
                setScrollLocked(false);
              }}
              user={user}
            />
          </Navbar>
        }
      >
        <Outlet context={{ user }} />
        <Affix position={{ bottom: 20, right: 20 }}>
          <Transition transition="slide-up" mounted={scroll.y > 800 && !opened}>
            {(transitionStyles) => (
              <Button
                leftIcon={<ArrowUp size={16} />}
                style={transitionStyles}
                onClick={() => scrollTo({ y: 0 })}
              >
                Scroll to Top
              </Button>
            )}
          </Transition>
        </Affix>
      </AppShell>
      <UnclosedSessionModal
        opened={open}
        sessions={sessions}
        onResolved={() => {
          setOpen(false);
        }}
      />
    </SpotlightProvider>
  );
}

export default Layout;
